import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { supabase } from "@/lib/supabase"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

/**
 * Whether the current user may reactivate this person: Admin role, and the
 * person is in their team (or they're in the Admin department). The same
 * public.can_reactivate_person check the reactivate-person edge function
 * enforces, so the button only shows when the request would be allowed.
 */
export const useCanReactivatePerson = (personUuid: string, enabled: boolean) =>
  useQuery({
    queryKey: ["can-reactivate-person", personUuid],
    enabled: enabled && !!personUuid,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("can_reactivate_person", {
        p_person_id: personUuid,
      })
      if (error) throw new Error(error.message)
      return data === true
    },
    // Permissions change rarely, and the edge function enforces them anyway.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })

/**
 * Reactivate an offboarded person: back to Active, with the offboarding
 * reason, notes and contract end date cleared. See the reactivate-person
 * edge function.
 */
export const useReactivatePerson = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (personUuid: string) => {
      const { error } = await supabase.functions.invoke("reactivate-person", {
        body: { personUuid },
      })

      if (error) {
        const message = await getSupabaseFunctionErrorMessage(error)
        throw new Error(message || "Failed to reactivate person")
      }
    },
    onSuccess: (_, personUuid) => {
      queryClient.invalidateQueries({ queryKey: ["people"] })
      queryClient.invalidateQueries({ queryKey: ["person", personUuid] })

      toast.success("Success", {
        description: "Person reactivated",
      })
    },
    onError: (error: Error) => {
      toast.error("Error", {
        description: error.message,
      })
    },
  })
}
